import { Colors } from "../../../utils/colors";
import { useCallback, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import RectangleButton from "../../Buttons/RectangleButton";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../../Containers/PopupModalContainer";
import CustomTextField from "../../CustomTextField";
import {
  JSONContent,
  MembershipPlan,
  NotificationType,
  OnboardingFlowType,
} from "@markit/common.types";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import AlertContainer from "../../Containers/AlertContainer";
import { InputAdornment, TextField } from "@mui/material";
import { useTheme } from "../../../hooks/useTheme";
import { getNumMembershipSubscriptionMembers } from "../../../utils/subscriptionUtils";
import { MARKIT_DOCS, hasSubscription } from "@markit/common.utils";
import { Icon } from "@iconify/react";
import {
  changeMembershipPlan,
  createMembershipPlan,
} from "../../../utils/membershipUtils";
import SwitchToggleItem from "../../SwitchToggleItem";
import NumberInput from "../../NumberInput";
import { useRichTextEditor } from "../../../hooks/useRichTextEditor";
import { useEditorState } from "@tiptap/react";
import CustomRichTextEditor from "../../TextFields/CustomRichTextEditor";
import OnboardingTourStep from "../../Onboarding/OnboardingTourStep";

export type MembershipPlanModalProps = {
  currMembershipPlan: MembershipPlan;
  setCurrMembershipPlan: (
    currMembershipPlan: MembershipPlan | undefined
  ) => void;
  tourStepNumber: number;
  tourTotalSteps: number;
};

const MembershipPlanModal = (props: MembershipPlanModalProps) => {
  const {
    currMembershipPlan,
    setCurrMembershipPlan,
    tourStepNumber,
    tourTotalSteps,
  } = props;
  const { accountData } = useSelector(getAccountState).account;
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const [savingChanges, setSavingChanges] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [alertText, setAlertText] = useState({
    header: "",
    subHeader: "",
  });
  const [originalMembershipPlan] = useState(currMembershipPlan);
  const [showGroupMembers, setShowGroupMembers] = useState(
    !!currMembershipPlan.groupMembers
  );
  const [planModalTourStep, setPlanModalTourStep] = useState(1);

  const styles = {
    secondaryButton: {
      whiteSpace: "nowrap",
      padding: 14,
      backgroundColor: Colors.WHITE1,
      border: `0.5px solid ${Colors.GRAY2}`,
    },
  };

  const { editor } = useRichTextEditor({
    body: currMembershipPlan.body,
    styles: `height: 160px; overflow: scroll; color: ${theme.PrimaryText.color};`,
    addCharacterCountExtension: {
      include: true,
      limit: 1000,
    },
    alternatePlaceholder: "Write a brief description...",
    theme,
  });

  const { charactersCount } = useEditorState({
    editor,
    selector: (context) => ({
      charactersCount: context.editor.storage.characterCount.characters(),
    }),
  });

  const disabledPrice = useMemo(
    () => currMembershipPlan.id === "free",
    [currMembershipPlan.id]
  );

  const updateSelectedMembershipPlan = useCallback(
    (newPlanSettings: Partial<MembershipPlan>) => {
      if (!currMembershipPlan) return;
      const finalPlan: MembershipPlan = {
        ...currMembershipPlan,
        ...newPlanSettings,
      };
      setCurrMembershipPlan({ ...finalPlan });
    },
    [currMembershipPlan, setCurrMembershipPlan]
  );

  const saveMembershipPlan = useCallback(async () => {
    setSavingChanges(true);

    const cleanedPlanName = currMembershipPlan.name.trim();
    if (cleanedPlanName === "") {
      setAlertText({
        header: "Plan Name is Required",
        subHeader: "Please enter a name for the plan.",
      });
      setSavingChanges(false);
      return;
    } else if (
      accountData.membership.membershipPlans.some(
        (plan) =>
          plan.name === cleanedPlanName && plan.id !== currMembershipPlan.id
      )
    ) {
      setAlertText({
        header: "Plan Name Already Exists",
        subHeader: "Please enter a unique name for the plan.",
      });
      setSavingChanges(false);
      return;
    } else if (editor.isEmpty) {
      setAlertText({
        header: "Plan Description is Required",
        subHeader: "Please enter a description for the plan.",
      });
      setSavingChanges(false);
      return;
    } else if (
      currMembershipPlan.id !== "free" &&
      currMembershipPlan.price < 100
    ) {
      setAlertText({
        header: "Plan price must be at least $1",
        subHeader: "Please enter a price for the plan.",
      });
      setSavingChanges(false);
      return;
    } else if (currMembershipPlan.price > 10000000) {
      setAlertText({
        header: "Plan price must be less than $10000",
        subHeader: "Please enter a lower price for the plan.",
      });
      setSavingChanges(false);
      return;
    } else if (
      currMembershipPlan.groupMembers < 0 ||
      currMembershipPlan.groupMembers > 12
    ) {
      setAlertText({
        header: "Group Plan Members must be between 1 and 12",
        subHeader: "Please enter a valid number of group plan members.",
      });
      setSavingChanges(false);
      return;
    }

    const editorData: JSONContent = editor.getJSON();
    const finalMembershipPlan: MembershipPlan = {
      ...currMembershipPlan,
      body: editorData,
    };

    // if the plan is new, create it
    if (finalMembershipPlan.id === "") {
      await createMembershipPlan(finalMembershipPlan, accountData.uid);
    } else {
      const numPaidMembers = await getNumMembershipSubscriptionMembers(
        accountData.uid,
        finalMembershipPlan.id
      );
      if (
        numPaidMembers > 0 &&
        originalMembershipPlan.price !== finalMembershipPlan.price
      ) {
        setAlertText({
          header: "You currently have paid members on this plan.",
          subHeader:
            "You can only edit the price if there are no paid members for the specific plan.",
        });
        setSavingChanges(false);
        return;
      }
      await changeMembershipPlan(finalMembershipPlan, accountData.uid);
    }
    setCurrMembershipPlan(undefined);
    showNotificationBanner(
      dispatch,
      finalMembershipPlan.id === "" ? "Plan Created" : "Saved Changes",
      NotificationType.AFFIRMATIVE
    );
    setSavingChanges(false);
  }, [
    currMembershipPlan,
    accountData.membership.membershipPlans,
    accountData.uid,
    editor,
    setCurrMembershipPlan,
    dispatch,
    originalMembershipPlan.price,
  ]);

  return (
    <>
      <PopupModalContainer
        headerComp={
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <span className="sectionTitle">Plan Details</span>
            <span className="smallBodySubtext">
              Edit plan name, price, and benefits
            </span>
          </div>
        }
        valueComp={
          <div
            className="ColumnNormal onboarding-paid-membership-details-target"
            style={{ gap: 20 }}
          >
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <span className="bodyMedium">Plan Name</span>
              <div className="ColumnNormal" style={{ gap: 4 }}>
                <CustomTextField
                  value={currMembershipPlan.name}
                  placeholder="Name Your Plan..."
                  inputMode="text"
                  borderRadius={12}
                  maxLength={60}
                  backgroundColor={Colors.WHITE1}
                  onChange={(change: any) => {
                    updateSelectedMembershipPlan({
                      name: change.target.value,
                    });
                  }}
                  altMarginBottom={0}
                />
              </div>
            </div>
            <div className="ColumnNormal" style={{ gap: 10 }}>
              <div className="AlignedRow" style={{ gap: 10 }}>
                <span className="bodyMedium">Monthly Price</span>
                <span
                  className="smallBodySubtext"
                  style={{ color: Colors.GRAY2 }}
                >
                  Price must be above $1
                </span>
              </div>
              <TextField
                size="small"
                type="number"
                placeholder={"10.00"}
                disabled={disabledPrice}
                value={
                  currMembershipPlan.id !== "free" &&
                  currMembershipPlan.price === 0
                    ? ""
                    : currMembershipPlan.price / 100
                }
                onChange={(e) => {
                  const roundedPriceInCents = Math.round(
                    (e.target.value as unknown as number) * 100
                  );
                  updateSelectedMembershipPlan({ price: roundedPriceInCents });
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                inputProps={{ min: 1 }}
                className="normalTextField"
                InputProps={{
                  style: {
                    fontSize: 14,
                    borderRadius: 12,
                    backgroundColor: Colors.WHITE1,
                    width: "100%",
                    borderColor: isFocused
                      ? theme.FocusedTextFieldBorder.borderColor
                      : disabledPrice
                        ? theme.TextFieldBorder.borderColor
                        : theme.EditableTextFieldBorder.borderColor,
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <span className="bodySubtext">$</span>
                    </InputAdornment>
                  ),
                }}
              />
              {currMembershipPlan.id !== "free" ? (
                <div className="ColumnNormal" style={{ gap: 4 }}>
                  <span
                    className="smallBodySubtext"
                    style={{ color: Colors.GRAY2 }}
                  >
                    Markit charges zero fees on memberships, other than standard
                    Stripe processing fees.{" "}
                    <span
                      style={{
                        fontWeight: 500,
                        color: Colors.GRAY1,
                        cursor: "pointer",
                      }}
                      onClick={() => window.open(MARKIT_DOCS + "/fees")}
                    >
                      Learn more{" "}
                      <Icon
                        icon="ion:chevron-forward"
                        height={12}
                        style={{ color: Colors.GRAY1, marginBottom: -2 }}
                      />
                    </span>
                  </span>
                </div>
              ) : null}
            </div>
            <div className="ColumnNormal" style={{ gap: 10 }}>
              <span className="bodyMedium">Plan Benefits</span>
              <div
                className="ColumnNormal"
                style={{
                  gap: 4,
                  border: `1px solid ${Colors.GRAY2}`,
                  padding: 14,
                  borderRadius: 12,
                }}
              >
                <CustomRichTextEditor
                  editor={editor}
                  headerComp={<></>}
                  isSimplified
                />
                <span
                  className="smallBodySubtext"
                  style={{ textAlign: "left" }}
                >
                  {charactersCount}/1000
                </span>
              </div>
            </div>
            <SwitchToggleItem
              title="Group Plan"
              description="Gives the ability for 1 main subscriber to pay for multiple subscribers under 1 paid membership (e.g. a family plan)"
              toggleValue={showGroupMembers}
              onChange={() => {
                setShowGroupMembers(!showGroupMembers);

                updateSelectedMembershipPlan({
                  groupMembers: showGroupMembers ? 0 : 1,
                });
              }}
              icon={
                <Icon
                  icon="ion:people-circle"
                  height={60}
                  style={{ color: Colors.BLACK }}
                />
              }
              theme={theme}
              disabled={currMembershipPlan.id !== ""}
            />
            {showGroupMembers ? (
              <div
                className="AlignedRowSpaced"
                style={{
                  gap: 20,
                  opacity: currMembershipPlan.id !== "" ? 0.3 : 1,
                }}
              >
                <div>
                  <div
                    className="bodyMedium"
                    style={{
                      ...theme.PrimaryText,
                    }}
                  >
                    Group Members
                  </div>
                  <div
                    className="smallBodySubtext"
                    style={{
                      ...theme.SecondaryText,
                      marginTop: 4,
                    }}
                  >
                    Set the number of additional people that are under the 1
                    main subscriber
                  </div>
                </div>
                <div style={{ width: "70%" }}>
                  <NumberInput
                    min={1}
                    max={12}
                    value={currMembershipPlan.groupMembers}
                    onChange={(value) =>
                      updateSelectedMembershipPlan({
                        groupMembers: value,
                      })
                    }
                    containerStyles={{
                      backgroundColor: Colors.GRAY6,
                    }}
                    disabled={currMembershipPlan.id !== ""}
                  />
                </div>
              </div>
            ) : null}
          </div>
        }
        fixedBottomContent={
          <div className="ColumnNormal">
            <HorizontalDivider altMargin={14} />
            <div className="AlignedRow" style={{ gap: 10, paddingInline: 14 }}>
              <div style={{ flex: 1 }}>
                <RectangleButton
                  buttonLabel={"Cancel"}
                  onPress={() => setCurrMembershipPlan(undefined)}
                  altTextColor={Colors.BLACK}
                  altColor={Colors.GRAY11}
                  containerStyles={styles.secondaryButton}
                />
              </div>
              <div
                style={{ flex: 1 }}
                className="onboarding-paid-membership-save-target"
              >
                <RectangleButton
                  buttonLabel={"Save"}
                  onPress={saveMembershipPlan}
                  altTextColor={Colors.WHITE}
                  altColor={Colors.BLACK}
                  containerStyles={{ paddingBlock: 14, paddingInline: 12 }}
                  loading={savingChanges}
                />
              </div>
            </div>
          </div>
        }
        noExit
        closeModal={() => setCurrMembershipPlan(undefined)}
        modalContentWidth={PopupModalContainerWidth.SMALL}
      />
      <AlertContainer
        headerComp={alertText.header}
        subHeaderComp={alertText.subHeader}
        closeModal={() => setAlertText({ header: "", subHeader: "" })}
        hideModal={alertText.header === ""}
      />
      {hasSubscription(accountData) ? (
        planModalTourStep === 1 ? (
          <OnboardingTourStep
            key="paid-membership-tour-step-details"
            flowType={OnboardingFlowType.PAID_MEMBERSHIP}
            target=".onboarding-paid-membership-details-target"
            content={
              <>
                Start by naming your plan, setting its price, and describing its
                benefits.
                <br />
                <br />
                If you want a &quot;family plan&quot; style membership, toggle
                on &quot;Group Plan&quot; below.
              </>
            }
            stepNumber={tourStepNumber}
            totalSteps={tourTotalSteps}
            placement="left"
            onStepComplete={() => setPlanModalTourStep(2)}
          />
        ) : (
          <OnboardingTourStep
            key="paid-membership-tour-step-save"
            flowType={OnboardingFlowType.PAID_MEMBERSHIP}
            target=".onboarding-paid-membership-save-target"
            content={
              <>
                You can create multiple plans to offer different tiers with
                various offerings.
              </>
            }
            stepNumber={tourStepNumber + 1}
            totalSteps={tourTotalSteps}
            placement="left"
          />
        )
      ) : null}
    </>
  );
};

export default MembershipPlanModal;
